import { MenuRow, MenuSurface } from '~/design-system/components/v1_5/Header';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { DIRECTORY_MENU_WIDTH } from './KindMenu';
import type { DirectoryAddItem, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The five rows, in order. The first three are the kinds you make; the last two are the two ways
 * something arrives from outside.
 *
 * Paper draws a 1px divider between those two groups. It is not built (D10) and neither is a
 * spacer: there are no dividers anywhere in v1.5, and Beka removed the 4px group spacers from every
 * menu this session, so this is one continuous run. A rule saying it again is a second reading
 * order laid over the one the spacing already gives.
 *
 * Nor are there shortcut hints. Paper prints ⌘1–⌘3 on the first three rows, but nothing in this build
 * binds them and nothing may: the browser owns ⌘1–⌘9 for switching tabs. Header rule 5 covers a
 * printed key exactly as it covers a button.
 */
const ROWS: Array<{
  id: DirectoryAddItem;
  icon: string;
  label: string;
  /** A real product mark, not a glyph: it keeps its own colours instead of taking `currentColor`. */
  brand?: boolean;
}> = [
  { id: 'artifact', icon: 'slideshow-view', label: 'New artifact' },
  { id: 'document', icon: 'file-text', label: 'New document' },
  { id: 'website', icon: 'global', label: 'Add website' },
  { id: 'images', icon: 'image-upload', label: 'Upload images' },
  { id: 'figma', icon: 'figma', label: 'Import from Figma', brand: true },
];

export interface DirectoryAddMenuProps {
  onAdd?: (item: DirectoryAddItem) => void;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The Directory's own `+` menu — 196px and five rows of its own, NOT the application header's
 * 332px `AddMenu` (D8).
 *
 * The two are different menus doing different jobs and the widths are the tell. The header's add
 * puts something on the canvas; this one adds to the kind you are looking at, which is why its rows
 * are "New artifact" rather than "Artifact" and why it ends with the two import routes instead of
 * with "Ask the agent to make one".
 */
export function DirectoryAddMenu({ onAdd, theme: themeProp, className }: DirectoryAddMenuProps) {
  const theme = useV15Theme(themeProp);
  return (
    <MenuSurface width={DIRECTORY_MENU_WIDTH} theme={theme} label="Add" className={className}>
      {ROWS.map((row) => (
        <MenuRow
          key={row.id}
          // A brand mark goes through `leading` rather than `icon` because `MenuRow`'s own glyph
          // path lets `DeckIcon` rewrite every fill to `currentColor` — which is right for a
          // monochrome glyph and turns the Figma logo into a grey smudge.
          icon={row.brand ? undefined : row.icon}
          leading={row.brand ? <DeckIcon name={row.icon} size={16} preserveColors /> : undefined}
          label={row.label}
          theme={theme}
          onClick={() => onAdd?.(row.id)}
        />
      ))}
    </MenuSurface>
  );
}
